import { useState } from 'react';
import { useUser } from '@/contexts/UserContext';
import { useConsolidatedProfile } from '@/hooks/useConsolidatedProfile';
import { useFamilyInviteCode } from '@/hooks/useFamilyInviteCode';
import { toast } from 'sonner';

/**
 * Main hook that provides all profile-related data
 * Now optimized to use consolidated fetching to reduce API calls
 */
export const useProfileData = () => {
  const { user } = useUser();
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  
  // Use the new consolidated profile hook for most data
  const { 
    profile, 
    familyMembers,
    inviteCode: consolidatedInviteCode,
    isFamilyAdmin,
    familyAdmin,
    isSingleMemberFamily,
    canSwitchFamily,
    switchFamilyWarning,
    isLoading: consolidatedLoading, 
    error: consolidatedError,
    refetchProfile 
  } = useConsolidatedProfile();
  
  // Keep the family invite code hook for invite code generation functionality
  const { 
    inviteCode: fallbackInviteCode, 
    isGeneratingCode, 
    generateNewInviteCode: originalGenerateNewInviteCode, 
    isLoading: codeLoading,
    error: codeError
  } = useFamilyInviteCode(user?.id);
  
  // Use invite code from consolidated data if available, otherwise fallback
  const inviteCode = consolidatedInviteCode || fallbackInviteCode;
  
  // Combine loading states - consolidated loading is the main one
  const isLoading = consolidatedLoading || codeLoading;
  
  // Determine if there's an error
  const error = consolidatedError || codeError || null;
  
  // If we have a profile error, but user is logged in, allow retrying
  const retryProfileLoad = () => {
    if (consolidatedError && user?.id) {
      toast.info('Retrying profile load...');
      refetchProfile();
    }
  };

  // Wrap generateNewInviteCode to return void
  const generateNewInviteCode = async (): Promise<void> => {
    await originalGenerateNewInviteCode();
  };

  return {
    isLoading,
    isCreatingProfile,
    profile,
    familyMembers,
    inviteCode,
    isSingleMemberFamily,
    isFamilyAdmin,
    familyAdmin,
    canSwitchFamily,
    switchFamilyWarning,
    error,
    generateNewInviteCode,
    isGeneratingCode,
    retryProfileLoad
  };
};
